import React, { useState, useEffect, useRef } from "react";
import { isMobile } from "react-device-detect";
import RouteApi from "../Backend/Backend";
import { toast } from "react-toastify"; // Import Toastify for notifications
import { ReactTyped } from "react-typed"; // ✅ correct
import { useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { DEFAULT_SCHOOL_ABREV } from "../config/school";

const Footer = () => {
  const { id } = useParams(); // Retrieve the dynamic id from the route params

  const location = useLocation();

  const {
    getStudents,
    handleStudents,
    handleTeachers,
    handleSubjects,
    getAiResponse,
    handleuploadFiles,
    getNotifications,
  } = RouteApi(); // Destructure function from RouteApi

  const [task, setTask] = useState("");

  const [students, setStudents] = useState([
    {
      firstName: "",
      lastName: "",
      parentEmail: "",
      parentPhone: "",
      studentClass: "",
      department: "",
      gender: "",
      state: "",
      school: "",
      ad_no: "",
    },
  ]);
  
  // ✅ Handle input change
  const handleInputChange = (index, event) => {
    const { name, value } = event.target;
    const newStudents = [...students];
    newStudents[index][name] = value;
    setStudents(newStudents);
  };
  
  // ✅ Validate multiple emails separated by ;
  const validateEmails = (emails) => {
    const emailList = emails
      .split(";")
      .map((e) => e.trim())
      .filter(Boolean);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailList.every((email) => emailRegex.test(email));
  };
  
  // ✅ Validate multiple phone numbers separated by ;
  const validatePhones = (phones) => {
    const phoneList = phones
      .split(";")
      .map((p) => p.trim())
      .filter(Boolean);
    const phoneRegex = /^\+?\d{7,15}$/; // allows + and 7–15 digits
    return phoneList.every((phone) => phoneRegex.test(phone));
  };
  
  // ✅ Add new student input fields
  const addStudent = () => {
    setStudents([
      ...students,
      {
        firstName: "",
        lastName: "",
        parentEmail: "",
        parentPhone: "",
        studentClass: "",
        department: "",
        gender: "",
        state: "",
        school: "",
      },
    ]);
  };
  
  // ✅ Submit students
  const handleStudentSubmit = async (e) => {
    e.preventDefault();
  
    for (const s of students) {
      const emailList = s.parentEmail
        ? s.parentEmail.split(";").map((e) => e.trim()).filter(Boolean)
        : [];
      const phoneList = s.parentPhone
        ? s.parentPhone.split(";").map((p) => p.trim()).filter(Boolean)
        : [];
  
      // ✅ Validate format
      if (emailList.length > 0 && !validateEmails(s.parentEmail)) {
        Swal.fire({
          text: `Invalid parent email(s) detected for ${s.firstName || "a student"}.`,
          icon: "warning",
          confirmButtonText: "Ok, got it!",
          customClass: { confirmButton: "btn fw-bold btn-warning" },
        });
        return;
      }
  
      if (phoneList.length > 0 && !validatePhones(s.parentPhone)) {
        Swal.fire({
          text: `Invalid parent phone number(s) detected for ${s.firstName || "a student"}.`,
          icon: "warning",
          confirmButtonText: "Ok, got it!",
          customClass: { confirmButton: "btn fw-bold btn-warning" },
        });
        return;
      }
  
      // ✅ Check for allowed combinations only
      const emailCount = emailList.length;
      const phoneCount = phoneList.length;
  
      const isValidCombo =
        (emailCount === 1 && phoneCount === 1) || // one of each
        (emailCount === 2 && phoneCount === 2);   // two of each
  
      if (!isValidCombo) {
        Swal.fire({
          text: `Invalid contact info for ${s.firstName || "a student"}.
          Allowed combinations:
          1️⃣ One parent email + one phone number
          2️⃣ Two parent emails + two phone numbers`,
          icon: "error",
          confirmButtonText: "Ok, got it!",
          customClass: { confirmButton: "btn fw-bold btn-danger" },
        });
        return;
      }
    }
  
    // ✅ Transform data
    const formattedStudents = students.map((s) => {
      const emailList = s.parentEmail
        ? s.parentEmail.split(";").map((e) => e.trim()).filter(Boolean)
        : [];
      const phoneList = s.parentPhone
        ? s.parentPhone.split(";").map((p) => p.trim()).filter(Boolean)
        : [];
  
      return {
        firstName: s.firstName,
        lastName: s.lastName,
        parentEmail1: emailList[0] || null,
        parentEmail2: emailList[1] || null,
        parentPhone1: phoneList[0] || null,
        parentPhone2: phoneList[1] || null,
        studentClass: s.studentClass,
        department: s.department,
        gender: s.gender,
        state: s.state,
        school: s.school,
        ad_no: s.ad_no,
      };
    });
  
    try {
      const response = await handleStudents(formattedStudents, id);
  
      if (response.success) {
        Swal.fire({
          text: "Students uploaded successfully!",
          icon: "success",
          confirmButtonText: "Ok, got it!",
          customClass: { confirmButton: "btn fw-bold btn-success" },
        });
  
        setStudents([
          {
            firstName: "",
            lastName: "",
            parentEmail: "",
            parentPhone: "",
            studentClass: "",
            department: "",
            gender: "",
            state: "",
            school: "",
            ad_no: "",
          },
        ]);
      } else {
        Swal.fire({
          text: response.message || "Failed to upload students.",
          icon: "warning",
          confirmButtonText: "Ok, got it!",
          customClass: { confirmButton: "btn fw-bold btn-warning" },
        });
      }
    } catch (error) {
      Swal.fire({
        text: "An error occurred while uploading students.",
        icon: "error",
        confirmButtonText: "Ok, got it!",
        customClass: { confirmButton: "btn fw-bold btn-danger" },
      });
    }
  };  
  
  // ✅ Remove student input
  const removeStudent = (index) => {
    setStudents((prevStudents) => prevStudents.filter((_, i) => i !== index));
  };
  
  
  const [teachers, setTeachers] = useState([
    {
      firstName: "",
      lastName: "",
      teacherId: generateTeacherId(1),
      teach_type: "SubjectTeacher",
    },
  ]);

  // Function to generate unique Teacher ID based on index
  function generateTeacherId(index) {
    const idNumber = String(index).padStart(4, "0"); // Ensures a 4-digit format (e.g., 0001)
    return `${id}${idNumber}`; // `id` should be defined before calling this function
  }

  // Function to handle input changes
  const handleTeacherInputChange = (index, event) => {
    const { name, value } = event.target;
    const updatedTeachers = [...teachers];
    updatedTeachers[index][name] = value;
    setTeachers(updatedTeachers);
  };

  // Function to add a new teacher
  const addTeacher = () => {
    setTeachers([
      ...teachers,
      {
        firstName: "",
        lastName: "",
        teacherId: generateTeacherId(teachers.length + 1),
        teach_type: "SubjectTeacher",
      },
    ]);
  };

  // Function to remove a teacher
  const removeTeacher = (index) => {
    const updatedTeachers = teachers.filter((_, i) => i !== index);
    setTeachers(updatedTeachers);
  };

  // Function to handle form submission
  const handleTeacherSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await handleTeachers(teachers, id); // Call API with teacher data
      if (response.success) {
        toast.success("Teachers uploaded successfully!");
        setTeachers([
          {
            firstName: "",
            lastName: "",
            teacherId: generateTeacherId(1),
            teach_type: "SubjectTeacher",
          },
        ]); // Reset form
      } else {
        toast.error(response.message || "Failed to upload teachers.");
      }
    } catch (error) {
      toast.error("An error occurred while uploading teachers.");
    }
  };

  const [subjects, setSubjects] = useState([{ name: "", department: "NONE" }]);

  const handleAddSubject = () => {
    setSubjects([...subjects, { name: "", department: "NONE" }]);
  };

  const handleRemoveSubject = (index) => {
    const updatedSubjects = subjects.filter((_, i) => i !== index);
    setSubjects(updatedSubjects);
  };

  const handleSubjectChange = (index, event) => {
    const { name, value } = event.target;
    const updatedSubjects = [...subjects];
    updatedSubjects[index][name] = value;
    setSubjects(updatedSubjects);
  };

  const handleSubjectSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await handleSubjects(subjects, id); // Call API with subject data

      if (response.status === "Uploaded") {
        toast.success(response.message || "Subjects uploaded successfully!");
        setSubjects([
          {
            name: "",
            department: "NONE",
          },
        ]); // Reset form
      } else if (response.status === "exists") {
        toast.error(response.message); // Show specific error message from the response
      } else {
        toast.error(response.message || "Failed to upload subjects.");
      }
    } catch (error) {
      toast.error("An unexpected error occurred while uploading subjects.");
      console.error(error);
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const response = await handleuploadFiles(selectedFile, id); // Pass file + id

    if (response.status === "Uploaded") {
      toast.success(response.message || "Students uploaded successfully!");
    } else if (response.status === "exists") {
      toast.error(response.message || "File already exists.");
    } else {
      toast.error(response.message || "Failed to upload file.");
    }
  };

  const [promptInput, setPromptInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [showFormatted, setShowFormatted] = useState({}); // track which messages show formatted HTML

  const handleSubmitPrompt = async (e) => {
    e.preventDefault();
    if (!promptInput.trim()) {
      toast.error("Please enter a prompt.");
      return;
    }

    const userMsg = { role: "user", content: promptInput };
    const updatedHistory = [...chatHistory, userMsg];

    setChatHistory(updatedHistory);
    setIsLoading(true);
    setPromptInput("");

    try {
      const result = await getAiResponse(updatedHistory);
      const aiMsg = { role: "assistant", content: result };
      setChatHistory([...updatedHistory, aiMsg]);
      // Reset formatted view for new message
      setShowFormatted((prev) => ({ ...prev, [updatedHistory.length]: false }));

      if (typeof getStudents === "function") {   // ✅ SAFE CHECK
        await getStudents();
      }

    } catch {
      toast.error("Failed to fetch AI response.");
    }

    setIsLoading(false);
  };

  const formatAiResponseAsHtml = (text) => {
    if (!text) return "";
  
    const emojiMap = {
      ":smile:": "😄",
      ":laughing:": "😆",
      ":thumbsup:": "👍",
      ":fire:": "🔥",
      ":heart:": "❤️",
      ":rocket:": "🚀",
      ":clap:": "👏",
      ":thinking:": "🤔",
      ":star:": "⭐",
      ":bulb:": "💡",
      ":warning:": "⚠️",
      ":check:": "✅",
      ":x:": "❌",
    };
  
    let html = text
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/:[a-z_]+:/g, (m) => emojiMap[m] || m)
      .replace(/\*\*(.*?)\*\*/g, "<div class='fw-medium py-0'>$1</div>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/__(.*?)__/g, "<u>$1</u>")
      .replace(/~~(.*?)~~/g, "<del>$1</del>")
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        '<a href="$2" target="_blank" style="color:#007bff;text-decoration:none;">$1 🔗</a>'
      )
      .replace(
        /(^|\s)(https?:\/\/[^\s]+)/g,
        '$1<a href="$2" target="_blank" style="color:#007bff;text-decoration:none;">$2</a>'
      )
      .replace(/^>\s?(.*)$/gm, `<blockquote style="border-left:4px solid #ccc;padding-left:10px;margin:8px 0;color:#555;font-style:italic;">$1</blockquote>`)
      .replace(/^---$/gm, "<hr style='border:1px solid #ccc;margin:10px 0;'/>");
  
    // ✅ Proper Markdown Table Parser
    html = html.replace(
      /((?:\|[^\n]+\|\n?)+)/g,
      (match) => {
        const lines = match
          .trim()
          .split("\n")
          .filter((line) => line.trim().startsWith("|"));
        if (lines.length === 0) return match;
  
        // remove separator line like |---|---|
        const cleanLines = lines.filter((line) => !/^\|\s*-+/.test(line));
  
        const rows = cleanLines.map((line, i) => {
          const cols = line
            .split("|")
            .filter((c) => c.trim() !== "")
            .map((c) => {
              const tag = i === 0 ? "th" : "td";
              return `<${tag} style="padding:6px 10px;border:1px solid #ccc;text-align:left;">${c.trim()}</${tag}>`;
            })
            .join("");
          return `<tr>${cols}</tr>`;
        });
  
        const header = rows.shift() ?? "<tr><th></th></tr>";

        return `
          <div style="overflow-x:auto;margin:10px 0;">
            <table style="border-collapse:collapse;width:100%;font-size:14px;">
              <thead style="background:#f7f7f7;">${header}</thead>
              <tbody>${rows.join("")}</tbody>
            </table>
          </div>
        `;
      }
    );
  
    html = html
      .replace(/\[x\]\s(.*)/gi, '<li>✅ $1</li>')
      .replace(/\[\s\]\s(.*)/gi, '<li>☐ $1</li>')
      .replace(/(^|\n)([-*]\s.+(\n[-*]\s.+)*)/g, (match) => {
        const items = match
          .split("\n")
          .filter((i) => i.trim().startsWith("-") || i.trim().startsWith("*"))
          .map((i) => `<li>${i.replace(/^[-*]\s*/, "")}</li>`)
          .join("");
        return `<ul style="margin:6px 0 6px 20px;">${items}</ul>`;
      })
      .replace(/(^|\n)(\d+\.\s.+(\n\d+\.\s.+)*)/g, (match) => {
        const items = match
          .split("\n")
          .filter((i) => /^\d+\./.test(i.trim()))
          .map((i) => `<li>${i.replace(/^\d+\.\s*/, "")}</li>`)
          .join("");
        return `<ol style="margin:6px 0 6px 20px;">${items}</ol>`;
      })
      .replace(/^(#{1,6})\s*(.*)$/gm, (match, hashes, title) => {
        const level = hashes.length;
        const size = 22 - level * 2;
        return `<h${level} style="margin-top:10px;margin-bottom:6px;font-size:${size}px;color:#111;">${title}</h${level}>`;
      });
  
    html = html
      .split("\n")
      .map((line) => {
        if (
          /^<.*>.*<\/.*>$/.test(line.trim()) ||
          line.trim().startsWith("<ul") ||
          line.trim().startsWith("<ol") ||
          line.trim().startsWith("<table") ||
          line.trim().startsWith("<blockquote") ||
          line.trim().startsWith("<h")
        )
          return line;
        if (!line.trim()) return "";
        return `<p style="margin:4px 0;color:#333;">${line}</p>`;
      })
      .join("");
  
    return `<div style="color:#222;font-size:15px;line-height:1.6;">${html}</div>`;
  };
  

  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = async () => {
    try {
      const notes = await getNotifications(id); // If ID is required
      if (Array.isArray(notes)) {
        setNotifications(notes);
      } else {
        console.error("Invalid notification format", notes);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      toast.error("Failed to fetch notifications.");
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [id]); // Re-run when `id` changes

  const getIcon = (type) => {
    switch (type.toLowerCase()) {
      case "low_stock":
        return (
          <i className="bi bi-exclamation-triangle-fill text-danger fs-t"></i>
        );
      case "report":
        return (
          <i className="bi bi-file-earmark-text-fill text-primary fs-t"></i>
        );
      case "payment_declined":
        return <i className="bi bi-cash-stack text-danger fs-t"></i>;
      case "system_update":
        return <i className="bi bi-arrow-repeat text-secondary fs-t"></i>;
      case "product_edit":
        return <i className="bi bi-pencil-square text-info fs-t"></i>;
      case "review":
        return <i className="bi bi-chat-left-text text-success fs-t"></i>;
      default:
        return <i className="bi bi-bell text-dark fs-t"></i>;
    }
  };

  const timeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diff = Math.floor((now - past) / 1000 / 60); // in minutes

    if (diff < 1) return "Just now";
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const currentPath = location.pathname;

  const isCreatePage =
    currentPath === `/${id}/cbt/create` || currentPath === `/${id}/exercise/create`;


  return (
    <div>
      
      {!isCreatePage && (
        <div
          className="btn btn-purple4 fs-t text-white shadow-none btn-sm end-0 me-4 me-lg-6 rounded-pill float mb-3"
          data-bs-toggle="modal"
          data-bs-target="#add_new"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
        >
          <i className="bi bi-plus"></i>
        </div>
      )}

      <div
        className="modal"
        id="add_new"
        tabindex="-1"
        role="dialog"
        data-bs-backdrop="static"
        aria-labelledby="exampleModalCenterTitle"
        aria-hidden="true"
      >
        <div
          className={
            isMobile
              ? "modal-dialog modal-dialog-centered modal-fullscreen"
              : "modal-dialog modal-dialog-centered modal-fullscreen"
          }
          role="document"
        >
          <div
            className={isMobile ? "modal-content" : "modal-content"}
            style={isMobile ? { border: "#fff" } : { border: "#fff" }}
          >
            <div className="modal-header border-0 mt-2 px-6">
              <div className="fs-t text-purple2">
                <i className="bi bi-plus fs-t"></i> Upload Console
              </div>

              <button
                className="btn btn-dark btn-icon rounded-br-lg2 rounded-bl-sm2 rounded-t-lg2"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="bi bi-x-lg fw-bold fs-3 text-white"></i>
              </button>
            </div>
            <div className="modal-body py-0 mb-4">
              <div className="px-lg-4">
                <div className="row align-items-center">
                  <div className="col-lg-8">
                    <div className="">
                      <ul class="nav nav-lb-tab">
                        <li class="nav-item fs-4">
                          <a
                            class="nav-link active"
                            id="student-tab"
                            data-bs-toggle="tab"
                            href="#student"
                            role="tab"
                            aria-controls="student"
                            aria-selected="true"
                          >
                            Add Student
                          </a>
                        </li>

                        <li class="nav-item fs-4">
                          <a
                            class="nav-link"
                            id="teacher-tab"
                            data-bs-toggle="tab"
                            href="#teacher"
                            role="tab"
                            aria-controls="teacher"
                            aria-selected="true"
                          >
                            Add Teacher
                          </a>
                        </li>

                        <li class="nav-item fs-4">
                          <a
                            class="nav-link"
                            id="subject-tab"
                            data-bs-toggle="tab"
                            href="#subject"
                            role="tab"
                            aria-controls="subject"
                            aria-selected="true"
                          >
                            Add Subject
                          </a>
                        </li>
                      </ul>

                      <div class="tab-content" id="myTabContent">
                        <div
                          class="tab-pane fade show active"
                          id="student"
                          role="tabpanel"
                          aria-labelledby="student-tab"
                        >
                          <form
                            className="mt-5 mb-4 px-lg-3"
                            onSubmit={handleStudentSubmit}
                          >
                            {students.map((student, index) => (
                              <div key={index} className="row mb-4 rounded">
                                <div className="col-lg-5 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Student First Name
                                  </label>
                                  <input
                                    className="form-control form-control-md text-dark fs-4 rounded-pill"
                                    type="text"
                                    name="firstName"
                                    value={student.firstName}
                                    onChange={(e) =>
                                      handleInputChange(index, e)
                                    }
                                    placeholder="Enter student first name"
                                    required
                                  />
                                </div>

                                <div className="col-lg-5 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Student Last Name
                                  </label>
                                  <input
                                    className="form-control form-control-md text-dark fs-4 rounded-pill"
                                    type="text"
                                    name="lastName"
                                    value={student.lastName}
                                    onChange={(e) =>
                                      handleInputChange(index, e)
                                    }
                                    placeholder="Enter student last name"
                                    required
                                  />
                                </div>

                                {id === DEFAULT_SCHOOL_ABREV ?
                                  <div className="col-lg-2 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    School
                                  </label>
                                  <select
                                    className="form-control form-control-md text-dark fs-4 rounded-pill"
                                    name="school"
                                    value={student.school}
                                    onChange={(e) =>
                                      handleInputChange(index, e)
                                    }
                                    required
                                  >
                                    <option value="">Select School</option>
                                    <option value="LIASU">LIASU</option>
                                    <option value="AKESAN">AKESAN</option>
                                    <option value="TOLU ALUKO">TOLU ALUKO</option>
                                  </select>
                                  </div>
                                  :
                                  <></>
                                }
                                
                                {id === 'racy' ?
                                  <div className="col-lg-2 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Admission No.
                                  </label>
                                  <input
                                    className="form-control form-control-md text-dark fs-4 rounded-pill"
                                    type="text"
                                    name="ad_no"
                                    value={student.ad_no}
                                    onChange={(e) =>
                                      handleInputChange(index, e)
                                    }
                                    placeholder="Enter student Admission No."
                                    required
                                  />
                                  </div>
                                  :
                                  <></>
                                }

                                <div className="col-lg-5 mb-4">
                                  <label className="fs-4 text-dark mb-2">Parent Email(s)</label>
                                  <input
                                    className="form-control form-control-md text-dark fs-4 rounded-pill"
                                    type="text"
                                    name="parentEmail"
                                    value={student.parentEmail}
                                    onChange={(e) => handleInputChange(index, e)}
                                    placeholder="Enter parent email(s), separated by ;"
                                  />
                                </div>

                                <div className="col-lg-5 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Parent Phone No.
                                  </label>
                                  <input
                                    className="form-control form-control-md text-dark  fs-4 rounded-pill"
                                    type="text"
                                    name="parentPhone"
                                    value={student.parentPhone}
                                    onChange={(e) =>
                                      handleInputChange(index, e)
                                    }
                                    placeholder="Enter parent phone number"
                                  />
                                </div>

                                <div className="col-lg-2 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                     Gender
                                  </label>
                                  <select
                                    className="form-control form-control-md text-dark  fs-4 rounded-pill"
                                    name="gender"
                                    value={student.gender}
                                    onChange={(e) =>
                                      handleInputChange(index, e)
                                    }
                                    required
                                  >
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                  </select>
                                </div>

                                <div className="col-lg-4 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Student Class
                                  </label>
                                  <select
                                    className="form-control form-control-md text-dark  fs-4 rounded-pill"
                                    name="studentClass"
                                    value={student.studentClass}
                                    onChange={(e) =>
                                      handleInputChange(index, e)
                                    }
                                    required
                                  >
                                    <option value="">Select Class</option>
                                    <option value="Preparatory 1">
                                      Preparatory 1
                                    </option>
                                    <option value="Preparatory 2">
                                      Preparatory 2
                                    </option>
                                    <option value="Reception 1">
                                      Reception 1
                                    </option>
                                    <option value="Reception 2">
                                      Reception 2
                                    </option>
                                    <option value="PlayGroup">PlayGroup</option>
                                    <option value="Reception">
                                      Reception 2
                                    </option>
                                    <option value="NUR 1">NUR 1</option>
                                    <option value="NUR 2">NUR 2</option>
                                    <option value="KG 1">KG 1</option>
                                    <option value="KG 2">KG 2</option>
                                    <option value="Grade 1">Grade 1</option>
                                    <option value="Grade 2">Grade 2</option>
                                    <option value="Grade 3">Grade 3</option>
                                    <option value="Grade 4">Grade 4</option>
                                    <option value="Grade 5">Grade 5</option>
                                    <option value="Grade 6">Grade 6</option>
                                    <option value="Jss1">JSS 1</option>
                                    <option value="Jss2">JSS 2</option>
                                    <option value="Jss3">JSS 3</option>
                                    <option value="sss1">SSS 1</option>
                                    <option value="sss2">SSS 2</option>
                                    <option value="sss3">SSS 3</option>
                                    {/* Add more options as needed */}
                                  </select>
                                </div>

                                <div className="col-lg-4 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Student Department
                                  </label>
                                  <select
                                    className="form-control form-control-md text-dark  fs-4 rounded-pill"
                                    name="department"
                                    value={student.department}
                                    onChange={(e) =>
                                      handleInputChange(index, e)
                                    }
                                    required
                                  >
                                    <option value="">Select Department</option>
                                    <option value="NONE">
                                      NONE
                                    </option>
                                    <option value="SCIENCE">SCIENCE</option>
                                    {id === 'sac' ?
                                    <>
                                    <option value="HUMANITIES">HUMANITIES</option>
                                    <option value="BUSINESS">
                                      BUSINESS
                                    </option>
                                    </>
                                    :
                                    <>
                                    <option value="ART">ART</option>
                                    <option value="COMMERCIAL">
                                      COMMERCIAL
                                    </option>
                                    </>
                                    }
                                  </select>
                                </div>

                                

                                <div className="col-lg-4 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Student State
                                  </label>
                                  <input
                                    className="form-control form-control-md text-dark  fs-4 rounded-pill"
                                    type="text"
                                    name="state"
                                    value={student.state}
                                    onChange={(e) =>
                                      handleInputChange(index, e)
                                    }
                                    placeholder="State of origin"
                                  />
                                </div>

                                {/* Other Input Fields Remain the Same */}

                                {index === 0 ? (
                                  <></>
                                ) : (
                                  <div className="col-lg-12 text-end">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger rounded-pill px-3"
                                      onClick={() => removeStudent(index)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}

                            <div className="row">
                              <div className="col-lg-6 mb-4">
                                <button
                                  type="submit"
                                  className="btn btn-md btn-purple rounded-pill"
                                >
                                  <i className="bi bi-check"></i> Upload
                                  Students
                                </button>
                              </div>

                              <div className="col-lg-6 mb-4 text-end">
                                <button
                                  type="button"
                                  className="btn btn-md btn-dark-primary rounded-pill"
                                  onClick={addStudent}
                                >
                                  <i className="bi bi-plus"></i> Add New
                                  Students
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                        <div
                          class="tab-pane fade"
                          id="teacher"
                          role="tabpanel"
                          aria-labelledby="teacher-tab"
                        >
                          <form
                            className="mt-5 mb-4 px-lg-3"
                            onSubmit={handleTeacherSubmit}
                          >
                            {teachers.map((teacher, index) => (
                              <div key={index} className="row mb-4 rounded">
                                {/* First Name */}
                                <div className="col-lg-6 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Teacher First Name
                                  </label>
                                  <input
                                    className="form-control form-control-md text-dark  fs-4 rounded-pill"
                                    type="text"
                                    name="firstName"
                                    value={teacher.firstName}
                                    onChange={(e) =>
                                      handleTeacherInputChange(index, e)
                                    }
                                    placeholder="Enter teacher first name"
                                    required
                                  />
                                </div>

                                {/* Last Name */}
                                <div className="col-lg-6 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Teacher Last Name
                                  </label>
                                  <input
                                    className="form-control form-control-md text-dark  fs-4 rounded-pill"
                                    type="text"
                                    name="lastName"
                                    value={teacher.lastName}
                                    onChange={(e) =>
                                      handleTeacherInputChange(index, e)
                                    }
                                    placeholder="Enter teacher last name"
                                    required
                                  />
                                </div>

                                {/* Teacher ID */}
                                <div className="col-lg-6 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Teacher ID
                                  </label>
                                  <input
                                    className="form-control form-control-md text-dark  fs-4 rounded-pill"
                                    type="text"
                                    id="name-input"
                                    name="teacherId"
                                    value={teacher.teacherId}
                                    onChange={(e) =>
                                      handleTeacherInputChange(index, e)
                                    }
                                    placeholder="Enter teacher ID"
                                    required
                                  />
                                </div>

                                {/* Teacher Type (Dropdown) */}
                                <div className="col-lg-6 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Teacher Type
                                  </label>
                                  <select
                                    className="form-control form-control-md text-dark  fs-4 rounded-pill"
                                    name="teach_type"
                                    value={teacher.teach_type}
                                    onChange={(e) =>
                                      handleTeacherInputChange(index, e)
                                    }
                                    required
                                  >
                                    <option value="">
                                      Select Teacher Type
                                    </option>
                                    <option value="SubjectTeacher">
                                      Subject Teacher
                                    </option>
                                    <option value="ClassTeacher">
                                      Class Teacher
                                    </option>
                                  </select>
                                </div>

                                {/* Remove Button (Only for extra teachers) */}
                                {index === 0 ? (
                                  <></>
                                ) : (
                                  <div className="col-lg-12 text-end">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger rounded-pill px-3"
                                      onClick={() => removeTeacher(index)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}

                            {/* Buttons */}
                            <div className="row">
                              <div className="col-lg-6 mb-4">
                                <button
                                  type="submit"
                                  className="btn btn-md btn-purple rounded-pill"
                                >
                                  <i className="bi bi-check"></i> Upload
                                  Teachers
                                </button>
                              </div>

                              <div className="col-lg-6 mb-4 text-end">
                                <button
                                  type="button"
                                  className="btn btn-md btn-dark-primary rounded-pill"
                                  onClick={addTeacher}
                                >
                                  <i className="bi bi-plus"></i> Add New Teacher
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                        <div
                          class="tab-pane fade"
                          id="subject"
                          role="tabpanel"
                          aria-labelledby="subject-tab"
                        >
                          <form
                            className="mt-5 mb-4 px-lg-3"
                            onSubmit={handleSubjectSubmit}
                          >
                            {subjects.map((subject, index) => (
                              <div key={index} className="row mb-4 rounded">
                                <div className="col-lg-6 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Subject Name
                                  </label>
                                  <input
                                    className="form-control form-control-md text-dark  fs-4 rounded-pill"
                                    type="text"
                                    name="name"
                                    value={subject.name}
                                    onChange={(e) =>
                                      handleSubjectChange(index, e)
                                    }
                                    placeholder="Enter subject name"
                                    required
                                  />
                                </div>

                                <div className="col-lg-6 mb-4">
                                  <label className="fs-4 text-dark mb-2">
                                    Subject Department
                                  </label>
                                  <select
                                    className="form-control form-control-md text-dark fs-4 rounded-pill"
                                    name="department"
                                    value={subject.department}
                                    onChange={(e) =>
                                      handleSubjectChange(index, e)
                                    }
                                    required
                                  >
                                    <option value="NONE">NONE</option>
                                    <option value="Science">Science</option>
                                    <option value="Art">Art</option>
                                    <option value="Commercial">
                                      Commercial
                                    </option>
                                    <option value="Graduated">
                                      Graduated
                                    </option>
                                  </select>
                                </div>

                                {index > 0 && (
                                  <div className="col-lg-12 text-end">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger rounded-pill px-3"
                                      onClick={() => handleRemoveSubject(index)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}

                            {/* Buttons */}
                            <div className="row">
                              <div className="col-lg-6 mb-4">
                                <button
                                  type="submit"
                                  className="btn btn-md btn-purple rounded-pill"
                                >
                                  <i className="bi bi-check"></i> Upload
                                  Subjects
                                </button>
                              </div>

                              <div className="col-lg-6 mb-4 text-end">
                                <button
                                  type="button"
                                  className="btn btn-md btn-dark-primary rounded-pill"
                                  onClick={handleAddSubject}
                                >
                                  <i className="bi bi-plus"></i> Add New Subject
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div className="text-center">
                      <div className="py-15">
                        <div className="rounded-4 border border-2 border-primary py-15 cursor-pointer">
                          <div className="display-1 text-primary">
                            <i class="bi bi-cloud-arrow-up"></i>
                          </div>
                          <div className="fs-t fw-medium text-dark mb-3">
                            Upload CSV or Excel File
                          </div>

                          <div className="fs-4 text-gray-600 mb-3">
                            Supported Format: CSV, Excel
                          </div>

                          <label className="btn btn-md rounded-4 btn-primary">
                            Upload File
                            <input
                              type="file"
                              accept=".csv, .xlsx"
                              onChange={handleFileChange} // <-- your function
                              style={{ display: "none" }}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="offcanvas offcanvas-end bg-white"
        tabIndex="-1"
        id="offcanvasRight"
        aria-labelledby="offcanvasRightLabel"
        role="dialog"
        style={
          isMobile
            ? {
              width: "1000px",
            }
            : {
                width: "500px",
              }
        }
      >
        <div className="offcanvas-header align-items-center shadow-none py-3 px-4">
          <div className="text-dark m-0 fs-3" id="offcanvasRightLabel">
            <i className="bi bi-app-indicator text-dark"></i> Notifications
          </div>
          <button
            type="button"
            className="btn btn-dark btn-icon rounded-br-lg2 rounded-bl-sm2 rounded-t-lg2"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="bi bi-x-lg fw-bold fs-4 text-white"></i>
          </button>
        </div>

        <div className="offcanvas-body px-3 py-0">
          {notifications.length > 0 ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                <span className="fw-medium text-dark fs-4">
                  All ({notifications.length})
                </span>
              </div>
              <div className="list-group">
                {notifications.map((note, idx) => (
                  <div>
                    <div
                      key={idx}
                      className="list-group-item list-group-item-action d-flex align-items-start rounded-3 mb-0 border-0 bg-white cursor-pointer"
                    >
                      <div className="icon flex-shrink-0">
                        {getIcon(note.type)}
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-semibold text-dark mb-1 fs-3">
                          {note.title}
                        </div>
                        <div className="text-dark fs-5">{note.message}</div>
                        <div className="text-secondary mt-1 small">
                          {timeAgo(note.created_at)}{" "}
                          {note.category ? `• ${note.category}` : ""}
                        </div>
                      </div>
                    </div>
                    <hr />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-muted">No notifications found.</p>
          )}
        </div>
      </div>

      <div
        class="offcanvas offcanvas-end bg-white border-0"
        tabindex="-1"
        id="smart-ai"
        aria-labelledby="offcanvasRightLabel"
        style={
          isMobile
            ? { width: "1000px",}
            : {
                width: "600px",
              }
        }
      >
        <div class="offcanvas-header align-items-center">
          <div className="fw-semibold fs-3 text-dark" id="offcanvasRightLabel">
            <i class="bi bi-stars text-warning fs-4"></i> Smart Ai
          </div>

          <button
            className="btn btn-purple4 btn-icon rounded-br-lg2 rounded-bl-sm2 rounded-t-lg2"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="bi bi-x-lg fw-bold fs-3 text-white"></i>
          </button>
        </div>
        <div className="offcanvas-body small">
        <div
            className="fs-4 text-dark mt-0 px-lg-2 py-2"
            style={{ minHeight: "60vh" }}
          >
            {chatHistory.length === 0 && !isLoading ? (
              <div
                className="text-center d-flex flex-column justify-content-center align-items-center"
                style={{
                  background: "url('/school-bg.svg') center/cover no-repeat",
                  borderRadius: "1rem",
                  padding: "3rem",
                  color: "#333",
                }}
              >
                <div className="fs-t3 mt-4 fw-medium text-purple3">
                  Ask something to get started...
                </div>
              </div>
            ) : (
              <>
                {chatHistory.map((msg, index) => {
                  const isAssistant = msg.role === "assistant";
                  const formattedHtml = formatAiResponseAsHtml(msg.content);
                  const plainText = msg.content.replace(/<\/?[^>]+(>|$)/g, "");

                  // 🧠 Copy to clipboard function
                  const handleCopy = () => {
                    navigator.clipboard.writeText(plainText);
                    const icon = document.getElementById(`copy-icon-${index}`);
                    if (icon) {
                      icon.classList.replace("bi-clipboard", "bi-check2");
                      setTimeout(() => {
                        icon.classList.replace("bi-check2", "bi-clipboard");
                      }, 1500);
                    }
                  };

                  return (
                    <div
                      key={index}
                      className={`d-flex w-100 mb-3 ${
                        isAssistant ? "justify-content-start" : "justify-content-end"
                      }`}
                    >
                      <div
                        className={`card shadow-none py-3 px-4 ${
                          isAssistant ? "bg-white text-dark" : "bg-purple text-white"
                        }`}
                        style={{
                          maxWidth: isAssistant ? "100%" : "80%",
                          borderRadius: "18px",
                          whiteSpace: "pre-wrap",
                          position: "relative",
                        }}
                      >
                        <div className="fw-semibold mb-2 d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2">
                            {isAssistant ? (
                              <>
                                <i className="bi bi-stars text-warning fs-3 me-2"></i>
                                <span className="text-dark fw-medium"></span>
                              </>
                            ) : (
                              <>
                                <i className="bi bi-chat-right-text text-light fs-5"></i>
                                <span className="text-white fw-medium fs-4"></span>
                              </>
                            )}
                          </div>

                          {/* ✅ Copy icon (only for assistant messages) */}
                          {isAssistant && (
                            <i
                              id={`copy-icon-${index}`}
                              className="bi bi-clipboard text-muted fs-5 cursor-pointer"
                              style={{
                                cursor: "pointer",
                                transition: "color 0.2s",
                              }}
                              onClick={handleCopy}
                              title="Copy response"
                              onMouseEnter={(e) => (e.currentTarget.style.color = "#6c63ff")}
                              onMouseLeave={(e) => (e.currentTarget.style.color = "#6c757d")}
                            ></i>
                          )}
                        </div>

                        {!showFormatted[index] && isAssistant ? (
                          <ReactTyped
                            strings={[plainText]}
                            typeSpeed={10}
                            backSpeed={0}
                            showCursor={false}
                            onComplete={() =>
                              setShowFormatted((prev) => ({
                                ...prev,
                                [index]: true,
                              }))
                            }
                          />
                        ) : (
                          <div
                            style={{
                              marginTop: isAssistant ? 6 : 0,
                              color: isAssistant ? "#222" : "#fff",
                              fontSize: "15px",
                              lineHeight: "1.6",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: isAssistant ? formattedHtml : msg.content,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}

                {isLoading && (
                  <div className="chat-bubble assistant fs-4 text-dark">
                    <em>Analysing result...</em>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="offcanvas-footer">
          <form onSubmit={handleSubmitPrompt} className="">
            <div className="px-lg-4 px-2 mb-2 mt-3 position-relative">
              <div className="d-flex align-items-center">
                <textarea
                  className="pe-7 px-lg-4 py-lg-3 form-control form-control-md bg-back border-none fs-4 text-dark rounded-4"
                  id="prompt"
                  name="prompt"
                  rows="3"
                  placeholder="Ask me anything..."
                  value={promptInput}
                  onChange={(e) => setPromptInput(e.target.value)}
                  disabled={isLoading}
                ></textarea>

                {/* ✅ Dynamic Button */}
                <div className="position-absolute top-50 end-0 translate-middle-y me-6">
                  {isLoading ? (
                    // Loader animation while AI is processing
                  
                    <button
                      className="btn btn-dark btn-icon rounded-circle text-white"
                      type="button" disabled
                    >
                      <div
                        className="spinner-border"
                        role="status"
                        >
                        <span className="visually-hidden">Loading...</span>
                        </div>
                    </button>

                  ) : promptInput.trim() === "" ? (
                    // Soundwave Icon when empty
                    <button
                      className="btn btn-dark btn-icon rounded-circle position-absolute top-50 end-0 translate-middle-y me-2 text-white"
                      type="button"
                      data-bs-toggle="modal"
                      data-bs-target="#voiceModal"
                    >
                      <i className="bi bi-soundwave fs-4"></i>
                    </button>
                  ) : (
                    // Send Icon when typing
                    <button
                      className="btn btn-dark btn-icon rounded-circle text-white"
                      type="submit"
                    >
                      <i className="bi bi-send-fill fs-4"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div
        className="modal fade"
        id="voiceModal"
        tabIndex="-1"
        aria-labelledby="voiceModalLabel"
        aria-hidden="true"
      >
        <div
          className={
            isMobile
              ? "modal-dialog modal-dialog-centered modal-fullscreen"
              : "modal-dialog modal-dialog-centered"
          }
        >
          <div className="modal-content border-0 rounded-4 shadow-lg">
            <div className="modal-header border-0 px-4 pt-3">
              <div className="fs-t fw-medium text-dark">
                <i class="bi bi-stars text-warning fs-4"></i>  Smart Ai Voice 
              </div>
              <button
                type="button"
                className="btn btn-dark btn-icon rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="bi bi-x-lg fw-bold fs-5 text-white"></i>
              </button>
            </div>

            <div className="modal-body text-center py-5">
              
              <div className="col-12 text-center">
                <div className="">
                  <div className="">
                    <img
                    src="../../assets/images/background/mic.svg"
                    alt="Recording"
                    width={300}
                    className="img-fluid mb-lg-4"
                   />
                  </div>

                  <div className="mt-4 mb-4">
                    <span className="fs-3 fw-medium text-dark">
                      Feature would be available soon.
                    </span>
                  </div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </div>



    </div>
  );
};

export default Footer;
